import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../elements/Modal';
import Icon from './Icon';
import { apiErrorText, apiRequest } from '../../utils';
import notificationConfig from '../../config/notificationConfig.json';
import './SendComposeModal.css';

/* Nothing leaves an event without somebody reading it first.

   Every send on these screens used to be a window.confirm: the admin pressed a button, both
   channels fired, and the first person to read the message was a recipient. This is the one
   screen that stands in front of all of them — publish, invite, remind, inform, and the survey
   request — because five near-identical composers is how they come to disagree.

   ★ THE MODAL IS THE CONFIRMATION. There is no confirm step behind it. A dialog that asks "are
   you sure?" after you have already read the message and the headcount is asking you to agree
   with yourself.

   Three things it is careful about:

   ★ The host edits a TEMPLATE, not one person's mail. `editable_body` keeps {{first_name}}; the
   preview pane shows it resolved for a real recipient. The two must not be confused — sending
   the resolved copy would greet everybody by the sample's name.

   ★ The preview is rendered in a SANDBOXED iframe. It is customer-authored HTML that our own
   admin is about to look at, and srcDoc without `sandbox` would run it on our origin. It also
   keeps the mail's CSS from leaking into the app around it.

   ★ The numbers are the server's, not a count of the rows on screen. "30 people, 24 will get
   the email" is the honest version of a button that used to say nothing at all. */

// The bell's sentence comes from the same i18n bundle the real notification feed uses, keyed by
// message name, so the preview cannot drift from what actually lands in the app.
const bellText = (t, messageName, dynamic) => (
  messageName ? t(`notifications:${messageName}`, { ...dynamic }) : ''
);

const bellIcon = (messageName) => (notificationConfig[messageName] || {}).icon || 'bell';

/* The mail as one document, for the iframe. `cta_url` is blank whenever the body is
   self-contained (an edited body, or an event sending its own texts), and the hosted SendGrid
   template hides its button in exactly that case — so the preview must hide it too. */
const previewDoc = (mail) => {
  if (!mail) return '';
  const cta = mail.cta_url
    ? `<p><a class="cta" href="${mail.cta_url}">${mail.cta_text || ''}</a></p>`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8">
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
         font-size: 15px; line-height: 1.5; color: #222; margin: 0; padding: 16px;
         background: #fff; }
  img { max-width: 100%; }
  .cta { display: inline-block; padding: 10px 20px; background: #eb5a40; color: #fff;
         border-radius: 4px; text-decoration: none; }
</style></head><body>${mail.body_start || ''}${cta}${mail.body_end || ''}</body></html>`;
};

const SendComposeModal = ({
  eventId, kind, segment, tags, ids, surveyId,
  sendPath, sendBody, title, onClose, onSent,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [note, setNote] = useState('');
  const [email, setEmail] = useState(true);
  const [bell, setBell] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(null);

  const descriptor = useMemo(() => ({
    kind, segment, tags: tags || [], ids: ids || [], survey_id: surveyId || null,
  }), [kind, segment, tags, ids, surveyId]);

  /* `withEdits` is false on the first call: the server then answers with the text that would
     otherwise have shipped, which is what the composer opens on. */
  const load = useCallback((withEdits) => {
    setBusy(true);
    setError(null);
    return apiRequest('POST', `/company/event/${eventId}/message/preview`, {
      ...descriptor,
      ...(withEdits ? { subject, body, note } : {}),
    })
      .then((res) => {
        setPreview(res);
        if (!withEdits) {
          setSubject(res.editable_subject || '');
          setBody(res.editable_body || '');
        } else {
          // The server sanitises on the way in, so what comes back may be less than what was
          // typed. Adopting it is what makes the box honest: the host sees what will be sent
          // rather than what they wrote.
          setBody(res.editable_body || '');
        }
        setStale(false);
        // An event with automatic mails off cannot email, whatever the box says.
        if (res.audience && res.audience.mail_off) setEmail(false);
      })
      .catch((err) => setError(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  }, [eventId, descriptor, subject, body, note, t]);

  useEffect(() => { load(false); /* on open only */ }, [eventId, kind]);   // eslint-disable-line react-hooks/exhaustive-deps

  const audience = (preview && preview.audience) || {};
  const mailOff = !!audience.mail_off;
  const nobody = audience.reached === 0;
  // null unless this same message already went out for this event in the last 24 hours.
  const already = (preview && preview.already_sent) || null;

  const send = () => {
    if (busy || nobody) return;
    setBusy(true);
    setError(null);
    apiRequest('POST', sendPath, {
      ...(sendBody || {}),
      segment: descriptor.segment,
      tags: descriptor.tags,
      ids: descriptor.ids,
      subject,
      body,
      note,
      channels: { email: email && !mailOff, notification: bell },
    })
      .then((res) => { onSent(res); onClose(); })
      .catch((err) => { setError(apiErrorText(t, err)); setBusy(false); });
  };

  const edited = (setter) => (e) => { setter(e.target.value); setStale(true); };
  const canSend = (email && !mailOff) || bell;

  return (
    <Modal show title={title || t('composeTitle')} onClose={onClose} hideFooter>
      <div className="scm">
        {/* Who it goes to — the server's numbers, not the rows on screen. */}
        <p className="scm__audience">
          <Icon name="users" />{' '}
          {t('composeAudience', { n: audience.reached != null ? audience.reached : '–' })}
          {audience.emailable != null && email && !mailOff
            && ` · ${t('composeEmailable', { n: audience.emailable })}`}
          {audience.suppressed > 0 && ` · ${t('composeOptedOut', { n: audience.suppressed })}`}
          {audience.no_email > 0 && ` · ${t('composeNoAddress', { n: audience.no_email })}`}
        </p>
        {audience.capped && <p className="scm__warn">{t('composeCapped')}</p>}

        {/* ★ "You already sent this." This is what stands where a rate limit used to: the route
            refused the 13th send in an hour, which is the platform overruling the person running
            the event. Telling them beats stopping them — they know something we do not, and the
            send goes ahead the moment they decide it should. Under an hour is the one worth
            noticing, so it gets its own sentence. */}
        {already && (
          <p className="scm__warn">
            <Icon name="clock" />{' '}
            {t(already.minutes_ago < 60 ? 'composeAlreadySentHour' : 'composeAlreadySentToday',
               { count: already.count })}
          </p>
        )}

        <div className="scm__channels">
          <label>
            <input
              type="checkbox"
              checked={email && !mailOff}
              disabled={mailOff}
              onChange={(e) => setEmail(e.target.checked)}
            />{' '}
            <Icon name="envelope" /> {t('channelEmail')}
          </label>
          <label>
            <input type="checkbox" checked={bell}
                   onChange={(e) => setBell(e.target.checked)} />{' '}
            <Icon name="bell" /> {t('channelNotification')}
          </label>
        </div>
        {/* Not a silent no-op: the per-event automatic-mails switch beats this screen, and a
            host who ticks the box and sees nothing arrive has no way to find out why. */}
        {mailOff && <p className="scm__warn">{t('composeMailOff')}</p>}
        {!canSend && <p className="scm__warn">{t('composeNoChannel')}</p>}

        {(email && !mailOff) && (
          <>
            <label className="scm__label" htmlFor="scm-subject">{t('composeSubject')}</label>
            <input id="scm-subject" type="text" maxLength={300}
                   value={subject} onChange={edited(setSubject)} />

            <label className="scm__label" htmlFor="scm-body">{t('composeBody')}</label>
            <textarea id="scm-body" rows={8} value={body} onChange={edited(setBody)} />
            <p className="scm__hint">{t('composeBodyHint')}</p>

            <div className="scm__row">
              <button type="button" className="bravos-copy"
                      disabled={busy} onClick={() => load(true)}>
                <Icon name="refresh" /> {t('composeUpdatePreview')}
              </button>
              {stale && <span className="scm__stale">{t('composeStale')}</span>}
            </div>

            {((preview && preview.warnings) || []).includes('noLink')
              && <p className="scm__warn">{t('composeNoLink')}</p>}

            <p className="scm__label">{t('composeWhatTheyGet')}</p>
            {preview && preview.email ? (
              <>
                <p className="scm__subject">{preview.email.subject}</p>
                {/* sandbox="" = no scripts, no forms, no same-origin. See the header note. */}
                <iframe className="scm__frame" title={t('composeWhatTheyGet')} sandbox=""
                        srcDoc={previewDoc(preview.email)} />
                <p className="scm__hint">{t('composeTemplateNote')}</p>
              </>
            ) : (
              !busy && <p className="scm__warn">{t('composePreviewFailed')}</p>
            )}
          </>
        )}

        {bell && (
          <>
            <p className="scm__label">{t('composeBellPreview')}</p>
            <div className="scm__bell">
              <Icon name={bellIcon(preview && preview.message_name)} />
              <span>
                {bellText(t, preview && preview.message_name,
                          (preview && preview.in_app && preview.in_app.dynamic) || {})}
              </span>
            </div>
            <label className="scm__label" htmlFor="scm-note">{t('composeNote')}</label>
            <input id="scm-note" type="text" maxLength={2000}
                   value={note} onChange={edited(setNote)} />
            <p className="scm__hint">{t('composeNoteHint')}</p>
          </>
        )}

        {error && <p className="scm__warn">{error}</p>}

        <div className="scm__actions">
          <button type="button" className="bravos-submit bravos-submit--ghost"
                  onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="bravos-submit"
                  disabled={busy || nobody || !canSend} onClick={send}>
            {busy ? t('sending') : t('composeSend', {
              n: audience.reached != null ? audience.reached : 0,
            })}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SendComposeModal;
